import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { useLazyFetchTradeChartsQuery, useLazyFetchTradesQuery } from '../api/app-dashboard.api';
import { AppDashboardAC, clearState, setCharts } from '../store/app-dashboard.slice';
import { DashboardTradeChartPayload, DashboardTradePayload } from '../types/app-dashboard.types';

interface UseAppDashboard {
  fetchTradesHandler: (payload: DashboardTradePayload) => Promise<void>;
  fetchTradeChartsHandler: (payload: DashboardTradeChartPayload) => Promise<void>;
  clearDashboardState: () => void;
}

export const useAppDashboard = (): UseAppDashboard => {
  const dispatch = useAppDispatch();

  const [fetchTradesQuery] = useLazyFetchTradesQuery();
  const [fetchTradeChartsQuery] = useLazyFetchTradeChartsQuery();

  const fetchTradesHandler = async (payload: DashboardTradePayload) => {
    dispatch(AppDashboardAC.toggleLoading(true));
    const { error, data: response } = await fetchTradesQuery(payload);
    dispatch(AppDashboardAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get trades!');
      return;
    } else {
      batch(() => {
        dispatch(AppDashboardAC.setItems(response.data));
        dispatch(AppDashboardAC.setPaginationMeta(response.meta));
      });
    }
  };

  const fetchTradeChartsHandler = async (payload: DashboardTradeChartPayload) => {
    const { error, data: response } = await fetchTradeChartsQuery(payload);
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get charts!');
      return;
    } else {
      dispatch(setCharts(response.data));
    }
  };

  const clearDashboardState = () => {
    dispatch(clearState());
  };

  return {
    fetchTradesHandler,
    fetchTradeChartsHandler,
    clearDashboardState,
  };
};
